import { observable, computed, action } from 'mobx';
import {
    InitialState,
    Contacts,
} from 'sletat-api-services/lib/sletat/models/initial-state';
import {
    CountryDetails,
    HotelDetails,
    HotelPageModel,
    HotelPopularity,
    ResortDetails,
} from 'sletat-api-services/lib/sletat/models/initial-state/page-models/hotel';
import { HotelPageMetaProps } from '../components/HotelPage/HotelPage';
import { KnownVisitSources } from '../../consts/knownVisitSources';

export class InitialStateStore {
    @observable
    private initialState: InitialState<HotelPageModel> | null = null;

    @observable
    private _previousSource: KnownVisitSources | null = null;

    @observable
    private _sourceType: string = 'hotel-page';

    constructor() {
        if (!isSSR) {
            this.initialState = window.__INITIAL_STATE__;
            this.tryLoadPreviousSource();
            this.loadSourceType();
        }
    }

    tryLoadPreviousSource(): void {
        if (window.location.search) {
            try {
                const params = new URLSearchParams(window.location.search);
                const prevSource = params.get('prev_source');
                if (prevSource && Object.values(KnownVisitSources).includes(prevSource as KnownVisitSources)) {
                    this.setPreviousSource(prevSource as KnownVisitSources);
                }
            } catch { console.error('Unexpected error when getting previous source of hotel page'); }
        }
    }

    loadSourceType(): void {
        if (window.location.search) {
                const params = new URLSearchParams(window.location.search);
                const soursType = params.get('source_type');
                if (soursType) {
                    this.setSourceType(soursType);
                }
        }
    }

    @action
    setSsrInitialState(
        initialState: InitialState<HotelPageModel>,
        additionalProps?: HotelPageMetaProps): void {
        this.initialState = initialState;
        if (additionalProps)
            this.setPreviousSource(additionalProps?.previousSource);
    }

    setPreviousSource(knownVisitSource: KnownVisitSources | null): void {
        this._previousSource = knownVisitSource;
    }

    setSourceType(sourceType: string): void {
        this._sourceType = sourceType;
    }

    @computed
    get countryDetails(): CountryDetails | null {
        return this.initialState?.pageModel?.countryDetails || null;
    }

    @computed
    get resortDetails(): ResortDetails | null {
        return this.initialState?.pageModel?.resortDetails || null;
    }

    @computed
    get hotelDetails(): HotelDetails | null {
        return this.initialState?.pageModel?.hotelDetails || null;
    }

    @computed
    get hotelPopularity(): HotelPopularity | null {
        return this.initialState?.pageModel?.hotelPopularity || null;
    }

    @computed
    get pageModel(): HotelPageModel | null {
        return this.initialState?.pageModel || null;
    }

    @computed
    get contacts(): Contacts | null {
        return this.initialState?.contacts || null;
    }

    @computed
    get geoCity(): GeoCity | null {
        return this.initialState?.geoCity || null;
    }

    @computed
    get touristDefaultPhone(): string | null {
        return this.contacts?.defaultContactPhones?.touristPhones
            ? this.contacts.defaultContactPhones.touristPhones[0]
            : null;
    }

    @computed
    get previousSource(): KnownVisitSources | null {
        return this._previousSource;
    }

    @computed
    get sourceType(): string {
        return this._sourceType;
    }
}
